import * as moment from 'moment-timezone';

/** Una pipa previa del mismo formulario: día de entrega (YYYY-MM-DD) y litros. */
export interface BatchPipe {
  date: string;
  liters: number | null | undefined;
}

export interface BatchProjectionInput {
  /** Inventario proyectado el día `startDate` (null = sin lectura: se restan enteras). */
  startStock: number | null | undefined;
  /** Día de esa lectura (YYYY-MM-DD). */
  startDate: string;
  /** Día de entrega de la pipa que se valida (YYYY-MM-DD). */
  targetDate: string;
  /** Venta diaria del producto (null o 0 = no se consume nada). */
  dailySale: number | null | undefined;
  /** Pipas previas del formulario (misma estación y producto, fecha ≤ targetDate). */
  pipes: BatchPipe[];
}

/**
 * Litros de las pipas PREVIAS del formulario que de verdad siguen en el tanque el día de entrega de la pipa que se
 * valida. Hasta el 16-oct se restaban enteras y una pipa el 18 + otra el 24 «excedía» aunque entre ambas fechas se
 * vende casi toda la primera (el teórico va topado en 0 y ese déficit no se ve). Se proyecta día a día desde el
 * inventario de la primera pipa previa con la venta diaria y piso en 0: lo que ocupan es la diferencia entre el
 * inventario CON esas pipas y SIN ellas. Espejo exacto de projectedBatchLiters del API (helpers/projected_batch.js):
 * si aquí cabe, allá no cae a revisión por espacio. Sin lectura de inventario se restan enteras, como antes.
 */
export function projectedBatchLiters({ startStock, startDate, targetDate, dailySale, pipes }: BatchProjectionInput): number {
  const flat = pipes.reduce((sum, p) => sum + (p.liters || 0), 0);
  if (startStock == null || !pipes.length) {
    return flat;
  }
  const byDay = new Map<string, number>();
  pipes.forEach((p) => byDay.set(p.date, (byDay.get(p.date) || 0) + (p.liters || 0)));
  const start = moment(startDate, 'YYYY-MM-DD');
  const target = moment(targetDate, 'YYYY-MM-DD');
  const sale = dailySale || 0;
  let withBatch = Math.max(0, startStock);
  let without = Math.max(0, startStock);
  for (const d = start.clone(); !d.isAfter(target, 'day'); d.add(1, 'day')) {
    // El inventario del día de la lectura ya trae su venta; de ahí en adelante se
    // descuenta la venta y luego entra la pipa que llega ese día.
    if (!d.isSame(start, 'day')) {
      withBatch = Math.max(0, withBatch - sale);
      without = Math.max(0, without - sale);
    }
    withBatch += byDay.get(d.format('YYYY-MM-DD')) || 0;
  }
  return Math.max(0, Math.round(withBatch - without));
}
